// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectModel } from '../model/project.model';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { DrillingholePresetVO } from '../actions/drilling-hole/drilling-hole.vo';
import { ArticlesCreator } from '../components/article/presenter/article-creator';
import { MountingRailArticlePostParseProcessor } from '../components/article/processor/postparse/mounting-rail-post-parse.processor';
import { VolatileDataSetHolder } from '../util/volatile-data-set.holder';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { DrillingHoleService } from '../actions/drilling-hole/drilling-hole.service';
import { DrillingHolePresetPresenter } from '@3d-scene/model3d/util/drillinghole/presenter/drilling-hole-preset.presenter';
import { ProjectArticleService } from './project-article.service';
import { MiniTerminalBlocksProjectCreationServiceDelegator } from '@configuration/wizard/service/mini-terminal-blocks-project-creation-service.delegator';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Injectable({providedIn: 'root'})
export class ProjectDrillingHoleService {

    constructor(
        private projectModel: ProjectModel,
        private drillingHoleService: DrillingHoleService,
        private articleDataService: ArticleDataService,
        private articleAccessoryManager: ArticleAccessoryManager,
        private projectArticleService: ProjectArticleService,
        private containerService: ContainerService,
        private drillingHolePresetPresenter: DrillingHolePresetPresenter,
        private miniTerminalBlocksProjectCreationServiceDelegator: MiniTerminalBlocksProjectCreationServiceDelegator,
        private articleUsageService: ArticleUsageService
    ) {}

  private get mountingRailArticleVO(): ArticleVO {
      return this.articleAccessoryManager.mountingRailArticleVO;
  }

  private set mountingRailArticleVO(value: ArticleVO) {
      this.articleAccessoryManager.mountingRailArticleVO = value;
  }

    public async loadDrillingholePresetWhenLoadingProject(): Promise<void> {
        if (this.mountingRailArticleVO && !this.projectModel.isMountingRailInProcess) {
          const drillingHolePreset = await this.articleDataService.loadDrillingHolePresetAfterProjectLoadedHandler({
            carrierArticleId: this.mountingRailArticleVO.identity,
            mode: ''
          });
          this.mountingRailArticleVO = null;
          await this.drillingholeLoadedSuccess( drillingHolePreset )
        }
      }

    private async drillingholeLoadedSuccess(drillingHolePreset: DrillingholePresetVO): Promise<void> {
        let hasArticleUsage: boolean = false;
        let mountingRailArticleUsage: ArticleUsageVO = null;
        for (const articleUsage of this.projectModel.articleUsageCache.values()) {
          if (this.articleUsageService.isMountingRailArticleUsage(articleUsage)) {
            mountingRailArticleUsage = articleUsage;
            hasArticleUsage = true;
            break;
          }
        }
        if (!hasArticleUsage) {
          mountingRailArticleUsage = this.projectArticleService.createNewArticleUsage(this.mountingRailArticleVO.identity, this.mountingRailArticleVO.version);
        }

        // always extend connection
        const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(mountingRailArticleUsage.id);
        if (articleContainer) {
          const railWasStretched: boolean = ArticlesCreator.updateRangeOnLineConnection(
            articleContainer,
            articleContainer.article,
            RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS
          );
          if (railWasStretched) {
            ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED, true);
            ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
              mountingRailArticleUsage,
              ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES,
              RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS_ON_BOTH_SIDES
            );
          } else {
            ArticleUsageUtils.removeArticleUsageInfoProperty(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED);
            ArticleUsageUtils.removeArticleUsageInfoProperty(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES);
          }
        }
        if (!drillingHolePreset) {
          await this.miniTerminalBlocksProjectCreationServiceDelegator.mountingRailAttached();
          this.drillingHoleService.notifyPresetCompleted(false);
          return;
        }
        VolatileDataSetHolder.instance.setDataSetValue(
          VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
          VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY,
          drillingHolePreset
        );

        // check if article has drilling hole preset
        if (!PropertyUtils.containsProperty(mountingRailArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET)) {
          ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET, true);
          if (!PropertyUtils.containsProperty(mountingRailArticleUsage.infoProperties, ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_DISTANCE)) {
            ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
              mountingRailArticleUsage,
              ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_DISTANCE,
              drillingHolePreset.distance
            );
          }
        }
        if (this.projectModel.projectMountingRailIsNew) {
          let railLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
            mountingRailArticleUsage,
            ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
          );
          if (NumberUtils.isNotNumber(railLength)) {
            railLength = MountingRailArticlePostParseProcessor.MAX_LENGTH;
          }
          await this.drillingHolePresetPresenter.createDrillingHolePreset(railLength / 10, 0, drillingHolePreset, mountingRailArticleUsage);
          this.projectModel.existingProjectMountingRail();
        }
    }
}
